import React from "react";
import { DIRECTIONS } from "./reminderConstants";

const DIRECTION_LABEL = Object.fromEntries(
  DIRECTIONS.map((d) => [d.value, d.label]),
);

/** One reminder-rule row. Memoized — re-renders only when its props change. */
const RuleRow = React.memo(function RuleRow({
  rule,
  savedQueryName,
  onEdit,
  onPreview,
  onToggle,
  onRemove,
}) {
  const schedule =
    rule.direction === "on"
      ? "on due date"
      : `${rule.offsetDays} day(s) ${DIRECTION_LABEL[rule.direction] || rule.direction}`;

  return (
    <li className="rem-item">
      <div className="rem-item-main">
        <span className="rem-item-name">{rule.name}</span>
        <span className="rem-item-meta">
          {savedQueryName} · {schedule} · {rule.frequency}
          {rule.sendTime
            ? ` · ${rule.sendTime}${rule.timezone ? ` ${rule.timezone}` : ""}`
            : ""}{" "}
          ·{" "}
          {rule.recipientMode === "explicit" ? "specific emails" : "owner"}
        </span>
      </div>
      <div className="rem-item-actions">
        <span className={rule.isEnabled ? "rem-badge rem-badge-on" : "rem-badge"}>
          {rule.isEnabled ? "Enabled" : "Disabled"}
        </span>
        <button
          type="button"
          className="rem-btn rem-btn-ghost"
          onClick={() => onEdit(rule)}
        >
          Edit
        </button>
        <button
          type="button"
          className="rem-btn rem-btn-ghost"
          onClick={() => onPreview(rule)}
        >
          Preview email
        </button>
        <button
          type="button"
          className="rem-btn rem-btn-ghost"
          onClick={() => onToggle(rule)}
        >
          {rule.isEnabled ? "Disable" : "Enable"}
        </button>
        <button
          type="button"
          className="rem-icon-btn"
          aria-label={`Delete ${rule.name}`}
          onClick={() => onRemove(rule.id)}
        >
          ×
        </button>
      </div>
    </li>
  );
});

/**
 * Lists the company's reminder rules with enable/disable and delete.
 * @param {object} props
 * @param {Array<object>} props.rules the reminder rules.
 * @param {Array<object>} props.savedQueries saved queries (to resolve names).
 * @param {(rule: object) => void} props.onEdit opens a rule for editing.
 * @param {(rule: object) => void} props.onPreview renders the rule's email.
 * @param {(rule: object) => void} props.onToggle flips a rule's enabled state.
 * @param {(id: number) => void} props.onRemove deletes a rule.
 * @returns {JSX.Element}
 */
export default function ReminderRuleList({
  rules,
  savedQueries,
  onEdit,
  onPreview,
  onToggle,
  onRemove,
}) {
  const nameById = new Map(savedQueries.map((q) => [q.id, q.name]));

  if (rules.length === 0) {
    return <p className="rem-muted">No reminder rules yet.</p>;
  }

  return (
    <ul className="rem-list">
      {rules.map((rule) => (
        <RuleRow
          key={rule.id}
          rule={rule}
          savedQueryName={nameById.get(rule.savedQueryId) || `Query #${rule.savedQueryId}`}
          onEdit={onEdit}
          onPreview={onPreview}
          onToggle={onToggle}
          onRemove={onRemove}
        />
      ))}
    </ul>
  );
}
